import { motion } from "framer-motion";
import { Heart, Leaf, Sparkles } from "lucide-react";

const VALUES = [
    {
        icon: Heart,
        title: "Bienveillance",
        text: "Une écoute attentive et sans jugement, à votre rythme et selon vos besoins.",
    },
    {
        icon: Leaf,
        title: "Approche naturelle",
        text: "Le respect de la physiologie, du lien et de votre intuition de parents.",
    },
    {
        icon: Sparkles,
        title: "Sur-mesure",
        text: "Chaque accompagnement est unique, pensé pour votre histoire singulière.",
    },
];

export default function About() {
    return (
        <section
            id="a-propos"
            data-testid="about-section"
            className="py-24 md:py-36 bg-sand/40"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9 }}
                        className="md:col-span-5"
                    >
                        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
                            <img
                                src="https://customer-assets.emergentagent.com/job_cindy-naissance/artifacts/m33dpmju_allaitement-d5e3e7cc52cfe0d7.jpeg"
                                alt="Bébé tendrement allaité par sa maman"
                                className="w-full h-full object-cover"
                                data-testid="about-image"
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9, delay: 0.1 }}
                        className="md:col-span-7"
                    >
                        <span className="text-xs tracking-[0.25em] uppercase text-rose-700">
                            À propos
                        </span>
                        <h2
                            className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink mt-4 leading-tight"
                            data-testid="about-title"
                        >
                            Cindy,
                            <br />
                            <span className="italic text-rose-700">à vos côtés</span>.
                        </h2>

                        <div className="mt-8 space-y-5 text-ink-soft leading-relaxed">
                            <p data-testid="about-paragraph-1">
                                Infirmière spécialisée dans l'accompagnement périnatal,
                                passionnée, j'ai choisi de consacrer mon métier au
                                soutien des familles à l'aube de leur nouvelle vie.
                            </p>
                            <p data-testid="about-paragraph-2">
                                Forte de mon expérience d'infirmière en salle
                                d'accouchement, suites de couches, maternité et
                                pédiatrie, j'ai pu observer de près les besoins
                                profonds des parents lors de l'arrivée d'un enfant.
                            </p>
                            <p data-testid="about-paragraph-3">
                                Parce que cette période de la vie est aussi
                                merveilleuse que bouleversante, et face au manque de
                                soutien auquel les familles font trop souvent face,
                                j'ai décidé de dédier ma pratique à l'accompagnement
                                périnatal. Je mets aujourd'hui mon expertise médicale
                                et mon écoute au service de votre parentalité, pour
                                vous guider pas à pas, à votre rythme.
                            </p>
                            <p data-testid="about-paragraph-4">
                                Mon objectif ? Vous entourer de bienveillance,
                                d'écoute et de conseils éclairés pour que vous
                                puissiez vivre cette transition essentielle en toute
                                sérénité.
                            </p>
                            <p data-testid="about-paragraph-5">
                                À Montpellier et dans ses alentours, j'offre une
                                présence douce, professionnelle et profondément
                                humaine.
                            </p>
                            <p data-testid="about-paragraph-6">
                                Mon rôle n'est pas de remplacer le corps médical,
                                mais de vous offrir un espace de parole, de
                                préparation et de réassurance — avant, pendant et
                                après la naissance. Mon approche s'inscrit dans le
                                respect de votre projet, de vos choix et de votre
                                intuition.
                            </p>
                        </div>

                        <div className="mt-12 grid sm:grid-cols-3 gap-6">
                            {VALUES.map((v, idx) => {
                                const Icon = v.icon;
                                return (
                                    <div
                                        key={v.title}
                                        data-testid={`about-value-${idx}`}
                                        className="border-l-2 border-rose-300 pl-5"
                                    >
                                        <Icon
                                            size={22}
                                            className="text-rose-700 mb-3"
                                            strokeWidth={1.5}
                                        />
                                        <h3 className="font-serif text-xl text-ink mb-1">
                                            {v.title}
                                        </h3>
                                        <p className="text-sm text-ink-soft leading-relaxed">
                                            {v.text}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
